import { useCallback, useLayoutEffect, useRef, type Ref, type TextareaHTMLAttributes } from 'react'
import { cn } from './cn'
import { useFieldControl } from './field-context'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Grow with the content (default true). */
  autoGrow?: boolean
  minRows?: number
  maxRows?: number
  invalid?: boolean
  ref?: Ref<HTMLTextAreaElement>
}

const LINE = 21
const PAD_Y = 18 // 8px top and bottom padding plus 1px borders

/** Multi line input that grows with its content (between minRows and maxRows). */
export function Textarea({
  autoGrow = true,
  minRows = 3,
  maxRows = 12,
  invalid,
  className,
  id,
  disabled,
  onInput,
  value,
  ref,
  ...rest
}: TextareaProps) {
  const control = useFieldControl({
    id,
    disabled,
    'aria-describedby': rest['aria-describedby'],
    'aria-invalid': invalid ?? (rest['aria-invalid'] as boolean | undefined),
  })
  const inner = useRef<HTMLTextAreaElement | null>(null)

  const resize = useCallback(() => {
    const el = inner.current
    if (!el || !autoGrow) return
    el.style.height = 'auto'
    const min = minRows * LINE + PAD_Y
    const max = maxRows * LINE + PAD_Y
    const next = Math.min(Math.max(el.scrollHeight + 2, min), max)
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight + 2 > max ? 'auto' : 'hidden'
  }, [autoGrow, minRows, maxRows])

  useLayoutEffect(() => {
    resize()
  }, [resize, value])

  const setRef = (node: HTMLTextAreaElement | null) => {
    inner.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = node
  }

  return (
    <textarea
      ref={setRef}
      rows={minRows}
      value={value}
      {...rest}
      id={control.id}
      disabled={control.disabled}
      aria-invalid={control['aria-invalid']}
      aria-describedby={control['aria-describedby']}
      aria-required={control['aria-required']}
      onInput={(e) => {
        resize()
        onInput?.(e)
      }}
      className={cn(
        'block w-full resize-none rounded-control border border-line-strong bg-surface px-3 py-2 text-body text-text',
        'shadow-none outline-none transition-[border-color,box-shadow] duration-150 ease-out placeholder:text-subtle',
        'focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus-halo',
        'aria-invalid:border-danger aria-invalid:focus-visible:ring-danger/20',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !autoGrow && 'resize-y',
        className,
      )}
    />
  )
}
